import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { money, shortDate } from "@/lib/format";
import { toast } from "sonner";
import { ArrowLeft, ChevronDown, ChevronRight, ExternalLink, Users2, Receipt, DollarSign, Pencil, Sparkles, Clock3, Ticket } from "lucide-react";
import OverrideEventModal from "@/components/OverrideEventModal";
import PartnerSignupLinkCard from "@/components/PartnerSignupLinkCard";

function currentPeriod() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export default function AdminPartnerDetail() {
  const { code } = useParams();
  const [period, setPeriod] = useState("");  // "" = lifetime
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [overrideEvent, setOverrideEvent] = useState(null);

  async function reload() {
    setLoading(true);
    try {
      const params = period ? { period } : {};
      const { data } = await api.get(`/commissions/partners/${code}/customer-rollup`, { params });
      setData(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load partner detail");
    } finally { setLoading(false); }
  }
  useEffect(() => { reload(); }, [code, period]);

  const toggle = (cid) => setExpanded((x) => ({ ...x, [cid]: !x[cid] }));

  return (
    <section className="space-y-8" data-testid="partner-detail-page">
      {/* Header */}
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <Link
            to="/admin"
            data-testid="back-to-overview"
            className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-cyan-400 mb-2 transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Overview
          </Link>
          <div className="text-xs uppercase tracking-widest text-cyan-400 font-semibold">
            Partner rollup
          </div>
          <h1 className="font-display text-4xl tracking-tight mt-1">
            {data?.partner?.name || code}
          </h1>
          <div className="text-sm text-slate-400 mt-1">
            <span className="font-mono">{code}</span>
            {data?.partner?.email && <> · {data.partner.email}</>}
            {data?.partner?.default_rate_pct != null && <> · Default rate {data.partner.default_rate_pct}%</>}
          </div>
        </div>
        <div className="flex gap-3 items-center flex-wrap">
          <Link
            to={`/admin/customers?partner=${encodeURIComponent(code)}`}
            data-testid="view-partner-customers-btn"
            className="inline-flex items-center gap-1.5 rounded-full border border-cyan-500/40 bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-300 px-4 py-2 text-xs font-semibold transition-colors"
            title={`View every ${data?.partner?.name || code} customer — including those without commissionable events yet`}
          >
            <Users2 className="w-3.5 h-3.5" />
            View all customers
          </Link>
          <select
            data-testid="period-picker"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="rounded-lg border border-slate-800 bg-slate-900 px-4 py-2.5 font-mono text-sm outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20"
          >
            <option value="">Lifetime</option>
            <option value={currentPeriod()}>Current month ({currentPeriod()})</option>
            {generateRecentPeriods(11).map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
        </div>
      </header>

      {data?.partner?.code && (
        <PartnerSignupLinkCard partnerCode={data.partner.code} partnerName={data.partner.name} transferUpstream={data.partner.transfer_upstream_name} />
      )}

      {loading && <div className="text-sm text-slate-500">Loading…</div>}

      {!loading && data && (
        <>
          {/* Totals strip */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <StatTile icon={Users2} label="Customers" value={data.totals.customer_count} testId="stat-customers" />
            <StatTile icon={Receipt} label="Commissionable events" value={data.totals.event_count} testId="stat-events" />
            <StatTile icon={DollarSign} label="Total ex-GST billed" value={money(data.totals.total_ex_gst)} testId="stat-ex-gst" />
            <StatTile
              icon={DollarSign}
              label="Total commission"
              value={money(data.totals.total_commission)}
              accent
              testId="stat-commission"
            />
          </div>

          {/* Pending customers — tickets attached to this partner that
              haven't produced any commissionable event yet. Fixes the
              "customer is tagged on a ticket but not on the partner page"
              gap reported in v1.10.124. */}
          {(data.pending_customers || []).length > 0 && (
            <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 overflow-hidden" data-testid="pending-customers-section">
              <div className="p-4 md:p-6 border-b border-amber-500/20 flex items-start justify-between gap-4">
                <div>
                  <h2 className="font-display text-xl tracking-tight inline-flex items-center gap-2">
                    <Clock3 className="w-5 h-5 text-amber-400" />
                    Awaiting first paid invoice
                    <span className="text-xs font-mono text-amber-300 bg-amber-500/15 border border-amber-500/30 px-2 py-0.5 rounded-full normal-case tracking-normal">
                      {data.pending_customers.length}
                    </span>
                  </h2>
                  <p className="text-sm text-amber-200/70 mt-0.5">
                    These customers are attached to <b>{data.partner?.name || code}</b> via a ticket / signup form but haven&apos;t produced a commissionable event yet. They&apos;ll move into the Customers table above the moment Syncro reports a paid invoice.
                  </p>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-950/40 text-xs uppercase tracking-wider text-amber-300/70 font-semibold">
                    <tr>
                      <th className="text-left px-6 py-3">Customer</th>
                      <th className="text-left px-6 py-3">Stage</th>
                      <th className="text-left px-6 py-3">Ticket</th>
                      <th className="text-left px-6 py-3">Source</th>
                      <th className="text-right px-6 py-3">Added</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-amber-500/10" data-testid="pending-customers-tbody">
                    {data.pending_customers.map((p) => (
                      <tr key={p.client_id} data-testid={`pending-customer-row-${p.client_id}`} className="hover:bg-amber-500/5 transition-colors">
                        <td className="px-6 py-3">
                          <Link
                            to={`/admin/tickets/${p.client_id}`}
                            className="font-medium text-slate-100 hover:text-cyan-300"
                          >
                            {p.business_name}
                          </Link>
                          {p.email && <div className="text-xs text-slate-500 mt-0.5">{p.email}</div>}
                        </td>
                        <td className="px-6 py-3">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full border border-slate-700 bg-slate-800 text-slate-300 text-xs font-mono">
                            {(p.stage || "not_contacted").replace(/_/g, " ")}
                          </span>
                        </td>
                        <td className="px-6 py-3">
                          {p.syncro_ticket_number ? (
                            p.syncro_ticket_url ? (
                              <a
                                href={p.syncro_ticket_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 font-mono text-xs text-cyan-400 hover:text-cyan-300 hover:underline"
                              >
                                <Ticket className="w-3 h-3" /> #{p.syncro_ticket_number} <ExternalLink className="w-3 h-3" />
                              </a>
                            ) : (
                              <span className="font-mono text-xs text-slate-300 inline-flex items-center gap-1">
                                <Ticket className="w-3 h-3" /> #{p.syncro_ticket_number}
                              </span>
                            )
                          ) : <span className="text-slate-600 text-xs">no Syncro ticket</span>}
                        </td>
                        <td className="px-6 py-3 text-xs text-slate-500">
                          {p.source || "—"}
                        </td>
                        <td className="px-6 py-3 text-right font-mono text-xs text-slate-500">
                          {shortDate(p.created_at)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Customer rollup */}
          <div className="rounded-xl border border-slate-800/80 bg-slate-900 overflow-hidden">
            <div className="p-4 md:p-6 border-b border-slate-800 flex items-center justify-between">
              <div>
                <h2 className="font-display text-xl tracking-tight">Customers</h2>
                <p className="text-sm text-slate-500 mt-0.5">
                  Click a row to expand the invoice list. Each invoice number links to Syncro for verification.
                </p>
              </div>
            </div>
            {data.customers.length === 0 ? (
              <div className="p-10 text-center text-slate-500">No commissionable events for this partner in the selected period.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-950/60 text-xs uppercase tracking-wider text-slate-500 font-semibold">
                    <tr>
                      <th className="text-left px-6 py-3 w-10"></th>
                      <th className="text-left px-6 py-3">Customer</th>
                      <th className="text-right px-6 py-3">Invoices</th>
                      <th className="text-right px-6 py-3">Ex-GST billed</th>
                      <th className="text-right px-6 py-3">Commission</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800" data-testid="customers-tbody">
                    {data.customers.map((c) => (
                      <>
                        <tr
                          key={c.customer_id}
                          onClick={() => toggle(c.customer_id)}
                          data-testid={`customer-row-${c.customer_id}`}
                          className="cursor-pointer hover:bg-slate-950/50 transition-colors"
                        >
                          <td className="px-6 py-3 text-slate-500">
                            {expanded[c.customer_id]
                              ? <ChevronDown className="w-4 h-4" />
                              : <ChevronRight className="w-4 h-4" />}
                          </td>
                          <td className="px-6 py-3 text-slate-100 font-medium">
                            {c.customer_name}
                            {c.customer_id !== "unknown" && (
                              <span className="text-xs text-slate-500 font-mono ml-2">#{c.customer_id}</span>
                            )}
                          </td>
                          <td className="px-6 py-3 text-right font-mono text-slate-300">{c.event_count}</td>
                          <td className="px-6 py-3 text-right font-mono text-slate-300">{money(c.total_ex_gst)}</td>
                          <td className="px-6 py-3 text-right font-mono font-medium text-cyan-400">{money(c.total_commission)}</td>
                        </tr>
                        {expanded[c.customer_id] && (
                          <tr key={c.customer_id + "-details"} className="bg-slate-950/40">
                            <td colSpan={5} className="px-6 py-4">
                              <InvoiceList
                                invoices={c.invoices}
                                onEdit={(inv) => setOverrideEvent({
                                  id: inv.event_id,
                                  commission_amount: inv.commission,
                                  original_commission_amount: inv.original_commission_amount,
                                  override_reason: inv.override_reason,
                                  payout_id: inv.payout_id,
                                })}
                              />
                            </td>
                          </tr>
                        )}
                      </>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {overrideEvent && (
        <OverrideEventModal
          event={overrideEvent}
          onClose={() => setOverrideEvent(null)}
          onSaved={() => { setOverrideEvent(null); reload(); }}
        />
      )}
    </section>
  );
}

function StatTile({ icon: Icon, label, value, accent, testId }) {
  return (
    <div className={`rounded-xl border p-5 ${accent ? "border-cyan-500/30 bg-cyan-500/5" : "border-slate-800 bg-slate-900"}`} data-testid={testId}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
          <div className={`font-mono text-2xl mt-2 ${accent ? "text-cyan-300 font-medium" : "text-slate-100"}`}>{value}</div>
        </div>
        <Icon className={`w-5 h-5 ${accent ? "text-cyan-400" : "text-slate-500"}`} />
      </div>
    </div>
  );
}

function InvoiceList({ invoices, onEdit }) {
  return (
    <div className="rounded-lg border border-slate-800 bg-slate-900 overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-slate-950/60 text-xs uppercase tracking-wider text-slate-500 font-semibold">
          <tr>
            <th className="text-left px-4 py-2">Paid</th>
            <th className="text-left px-4 py-2">Invoice</th>
            <th className="text-left px-4 py-2">Product</th>
            <th className="text-right px-4 py-2">Ex-GST</th>
            <th className="text-right px-4 py-2">Rate</th>
            <th className="text-right px-4 py-2">Commission</th>
            <th className="text-left px-4 py-2">Payout</th>
            <th className="text-right px-4 py-2"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-800">
          {invoices.map((i) => (
            <tr key={i.event_id} className={i.status === "voided" ? "opacity-50 line-through" : ""}>
              <td className="px-4 py-2 font-mono text-xs text-slate-400">{shortDate(i.paid_at)}</td>
              <td className="px-4 py-2">
                {i.invoice_id ? (
                  i.invoice_url ? (
                    <a
                      href={i.invoice_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`invoice-link-${i.event_id}`}
                      className="inline-flex items-center gap-1 font-mono text-xs text-cyan-400 hover:text-cyan-300 hover:underline"
                    >
                      #{i.invoice_id} <ExternalLink className="w-3 h-3" />
                    </a>
                  ) : (
                    <span className="font-mono text-xs text-slate-300">#{i.invoice_id}</span>
                  )
                ) : <span className="text-slate-600 text-xs">—</span>}
              </td>
              <td className="px-4 py-2">
                <div className="flex flex-wrap gap-1">
                  {(i.product_labels || []).map((lbl) => (
                    <span
                      key={lbl}
                      className="inline-flex items-center px-2 py-0.5 rounded-full border border-cyan-500/30 bg-cyan-500/10 text-cyan-300 text-xs font-medium"
                    >
                      {lbl}
                    </span>
                  ))}
                  {(i.product_labels || []).length === 0 && <span className="text-slate-600 text-xs">—</span>}
                </div>
              </td>
              <td className="px-4 py-2 text-right font-mono text-slate-300">{money(i.amount_ex_gst)}</td>
              <td className="px-4 py-2 text-right font-mono text-slate-500 text-xs">{i.rate_snapshot}%</td>
              <td className="px-4 py-2 text-right font-mono font-medium text-cyan-400">
                <div className="inline-flex items-center gap-1.5">
                  {i.original_commission_amount != null && (
                    <span
                      data-testid={`override-badge-${i.event_id}`}
                      title={`Original: $${Number(i.original_commission_amount).toFixed(2)}${i.override_reason ? " — " + i.override_reason : ""}`}
                      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border border-amber-500/40 bg-amber-500/10 text-amber-300 text-[10px] font-semibold uppercase tracking-wider"
                    >
                      <Sparkles className="w-3 h-3" /> ADJ
                    </span>
                  )}
                  {money(i.commission)}
                </div>
              </td>
              <td className="px-4 py-2">
                {i.payout_id ? (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full border border-slate-700 bg-slate-800 text-slate-300 text-xs font-mono">
                    payout #{i.payout_id.slice(0, 8)}
                  </span>
                ) : (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full border border-amber-500/30 bg-amber-500/10 text-amber-300 text-xs">
                    unpaid
                  </span>
                )}
              </td>
              <td className="px-4 py-2 text-right">
                {i.status !== "voided" && (
                  <button
                    data-testid={`override-invoice-${i.event_id}`}
                    onClick={() => onEdit?.(i)}
                    title="Override commission"
                    className="text-cyan-400 hover:text-cyan-300"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function generateRecentPeriods(count) {
  const out = [];
  const d = new Date();
  d.setDate(1);
  for (let i = 1; i <= count; i++) {
    d.setMonth(d.getMonth() - 1);
    out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  }
  return out;
}
